export interface ApiErrorDetail {
  code?: string;
  message?: string;
}

interface ApiValidationIssue {
  type?: unknown;
  loc?: unknown;
  msg?: unknown;
}

const FIELD_LABELS: Readonly<Record<string, string>> = {
  username: "登录用户名",
  name: "姓名",
  role: "角色",
  status: "账号状态",
  initial_password: "初始密码",
  current_password: "当前密码",
  new_password: "新密码",
  work_name: "作品名称",
  work_type_code: "作品类型",
  completion_date: "创作完成日期",
  publication_date: "创作发表日期",
  author_ids: "作者",
  right_holder_ids: "著作权人",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validationFieldLabel(location: unknown): string | undefined {
  if (!Array.isArray(location)) return undefined;
  const segments = location as unknown[];
  for (let index = segments.length - 1; index >= 0; index -= 1) {
    const segment: unknown = segments[index];
    if (typeof segment === "string" && FIELD_LABELS[segment]) return FIELD_LABELS[segment];
  }
  return undefined;
}

function normalizeValidationMessage(message: string): string {
  const normalized = message.replace(/^Value error,\s*/i, "").trim();
  return normalized === "Field required" ? "此项为必填项" : normalized;
}

function parseErrorDetail(value: unknown): ApiErrorDetail {
  if (typeof value === "string") return { message: value };

  if (Array.isArray(value)) {
    const messages = value.flatMap((rawIssue) => {
      if (!isRecord(rawIssue)) return [];
      const issue = rawIssue as ApiValidationIssue;
      if (typeof issue.msg !== "string") return [];
      const message = normalizeValidationMessage(issue.msg);
      const fieldLabel = validationFieldLabel(issue.loc);
      return [`${fieldLabel ? `${fieldLabel}：` : ""}${message}`];
    });
    const uniqueMessages = [...new Set(messages)];
    return {
      code: "VALIDATION_ERROR",
      message: uniqueMessages.length ? uniqueMessages.join("；") : undefined,
    };
  }

  if (isRecord(value)) {
    return {
      code: typeof value.code === "string" ? value.code : undefined,
      message: typeof value.message === "string" ? value.message : undefined,
    };
  }
  return {};
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function readCookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")
    .slice(1)
    .join("=");
}

export async function apiResponse(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");

  if (typeof init.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const method = (init.method ?? "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrfToken = readCookie("documentation_csrf");
    if (csrfToken) {
      headers.set("X-CSRF-Token", decodeURIComponent(csrfToken));
    }
  }

  const response = await fetch(path, { ...init, credentials: "include", headers });
  if (!response.ok) {
    let detail: ApiErrorDetail = {};
    try {
      const payload = (await response.json()) as { detail?: unknown };
      detail = parseErrorDetail(payload.detail);
    } catch {
      // The status and fallback message still describe non-JSON failures.
    }
    throw new ApiError(
      response.status,
      detail.code ?? "REQUEST_FAILED",
      detail.message ?? `请求失败（${response.status}）`,
    );
  }

  return response;
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await apiResponse(path, init);

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}
